import { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@mui/material';

import {
  ShiftDetailsPageContainer,
  ShiftDetailsCard,
  ShiftDetailsDivider,
  ShiftSeparatorDivider,
  ShiftDetailRow,
  DetailLabelGroup,
} from './ShiftDetails.styles';
import { calculateShiftHours } from '../../utils/shiftUtils';
import { useTimeFormatters } from '../../utils/Hooks/venue/useVenueTimeFormatters';
import { isShiftInPast, sortBreaks } from '../../utils/helpers/dashboard/dashboardHelpers';
import { getShiftSubtitle } from '../../utils/helpers/shifts/shiftsHelpers';
import { StatusText } from '../shared/SharedStyles';

// Components
import BreakDetails from './components/BreakDetails';

const ShiftDetails = ({ shifts }) => {
  // Use TZ formatters that automatically apply venue timezone
  const { formatTime } = useTimeFormatters();

  return (
    <ShiftDetailsPageContainer>
      {shifts.map((shift, index) => {
        const isPastShift = isShiftInPast(shift);
        const sortedBreaks = shift.breaks ? sortBreaks(shift.breaks, isPastShift) : [];
        const shiftStartSubtitleData = getShiftSubtitle(true, shift, formatTime);
        const shiftEndSubtitleData = getShiftSubtitle(false, shift, formatTime);
        const hasScheduledStartTimePassed = shift.start && shift.start < new Date();
        const hasScheduledEndTimePassed = shift.end && shift.end < new Date();

        return (
          <Fragment key={shift.id}>
            {/* Role and Hours */}
            <Typography variant="h6">
              {shift.name || 'Role not specified'} - {calculateShiftHours(shift)}
            </Typography>

            {/* Shift Details Box */}
            <ShiftDetailsCard>
              {/* Shift Start */}
              <ShiftDetailRow>
                <DetailLabelGroup>
                  <Typography variant="body2">Shift start</Typography>
                  <StatusText variant="caption">{shiftStartSubtitleData.shiftSubtitle}</StatusText>
                </DetailLabelGroup>
                {hasScheduledStartTimePassed && !shift.clockedInAt ? (
                  <StatusText variant="body2" status="error">
                    Missed
                  </StatusText>
                ) : (
                  <StatusText variant="body2" status="info">
                    {shift.clockedInAt ? formatTime(shift.clockedInAt) : 'Upcoming'}
                  </StatusText>
                )}
              </ShiftDetailRow>

              <ShiftDetailsDivider />

              {/* Break Information */}
              {sortedBreaks.map((breakData) => (
                <BreakDetails key={breakData.id} breakData={breakData} />
              ))}

              {/* Shift End */}
              <ShiftDetailRow>
                <DetailLabelGroup>
                  <Typography variant="body2">Shift end</Typography>
                  <StatusText variant="caption">{shiftEndSubtitleData.shiftSubtitle}</StatusText>
                </DetailLabelGroup>
                {hasScheduledEndTimePassed ? (
                  <StatusText variant="body2" status="error">
                    {shift.clockedOutAt ? formatTime(shift.clockedOutAt) : 'Missed'}
                  </StatusText>
                ) : (
                  <StatusText variant="body2" status="info">
                    {shift.clockedOutAt ? formatTime(shift.clockedOutAt) : 'Upcoming'}
                  </StatusText>
                )}
              </ShiftDetailRow>
            </ShiftDetailsCard>

            {index < shifts.length - 1 && <ShiftSeparatorDivider />}
          </Fragment>
        );
      })}
    </ShiftDetailsPageContainer>
  );
};

ShiftDetails.propTypes = {
  shifts: PropTypes.array.isRequired,
};

export default ShiftDetails;
